import { useEffect, useState } from 'react';
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { Form, FormInstance, GetProp, Image, message, Segmented, Upload, UploadFile, UploadProps } from 'antd';
import { Rule } from 'antd/lib/form';
import { RcFile } from 'antd/es/upload';
import ImgCrop from 'antd-img-crop';

import clsx from 'clsx';
import { useFileUpload } from '../../../hooks/api/files';

import styles from './index.module.scss';

/** Discrete crop presets (antd-img-crop aspectSlider is continuous only). */
const ASPECT_PRESETS = [
  { key: '1:1', label: '1:1', value: 1 },
  { key: '1200x674', label: '1200×674', value: 1200 / 674 }, // ≈ 16:9
  { key: '9:16', label: '9:16', value: 9 / 16 },
] as const;

type AspectPresetKey = (typeof ASPECT_PRESETS)[number]['key'];

const closestAspectPresetKey = (aspect: number): AspectPresetKey => (
  ASPECT_PRESETS.reduce((best, preset) => (
    Math.abs(preset.value - aspect) < Math.abs(best.value - aspect) ? preset : best
  )).key
);

const getAspectByKey = (key: AspectPresetKey) => (
  ASPECT_PRESETS.find((preset) => preset.key === key)?.value ?? 1
);

const beforeUpload = (file: Parameters<GetProp<UploadProps, 'beforeUpload'>>[0]) => {
  const isLt5M = file.size / 1024 / 1024 < 5;

  if (!isLt5M) {
    message.error('Image must smaller than 5MB!');

    return Upload.LIST_IGNORE;
  }

  return true;
};

interface FileProps {
  form: FormInstance;
  defaultLocation?: string;
  label?: string;
  name?: string | string[];
  rules?: Rule[];
  description?: string;
  valueKey?: 'id' | 'location'
  uploadAreaClasses?: string;
  uploadText?: string;
  /** Default crop aspect ratio (width / height). Defaults to 1 (square). */
  aspect?: number;
  /** Show Square / 1200×674 / 9:16 aspect presets. Defaults to true. */
  showAspectOptions?: boolean;
  onChange?: () => void;
}

export default function File({
  form,
  defaultLocation,
  name = 'fileId',
  rules = [{ required: true, message: 'Please enter icon' }],
  label,
  description,
  valueKey = 'id',
  uploadAreaClasses,
  uploadText = 'Upload Icon',
  aspect = 1,
  showAspectOptions = true,
  onChange,
}: FileProps) {
  const fileUpload = useFileUpload();
  const [isIgnoreDefault, setIsIgnoreDefault] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [aspectKey, setAspectKey] = useState<AspectPresetKey>(() => closestAspectPresetKey(aspect));
  const cropAspect = getAspectByKey(aspectKey);

  useEffect(() => {
    setAspectKey(closestAspectPresetKey(aspect));
  }, [aspect]);

  useEffect(() => {
    if (fileUpload?.data?.id) {
      form.setFieldValue(name, fileUpload?.data[valueKey]);
      setIsIgnoreDefault(false);
      // Form.setFieldValue does not trigger Form.onChange; notify parent after value is set.
      onChange?.();
    }
  }, [fileUpload?.data?.id]);

  const imageUrl = fileUpload?.data?.location || (isIgnoreDefault ? undefined : defaultLocation);

  const fileList: UploadFile[] = imageUrl
    ? [{
      uid: '-1',
      name: 'image',
      status: fileUpload.loading ? 'uploading' : 'done',
      url: imageUrl,
    }]
    : [];

  const uploadButton = (
    <div className={clsx(styles.uploadButton, uploadAreaClasses)}>
      {fileUpload.loading ? <LoadingOutlined /> : <PlusOutlined />}
      <div style={{ marginTop: 8 }}>{uploadText}</div>
    </div>
  );

  const handlePreview = async (file: UploadFile) => {
    setPreviewImage(file.url || '');
    setPreviewOpen(true);
  };

  const handleRemove = () => {
    fileUpload.clearResponse();
    form.setFieldValue(name, undefined);
    setIsIgnoreDefault(true);
    onChange?.();

    return true;
  };

  const isRequired = rules.some((rule) => typeof rule === 'object' && 'required' in rule && rule.required);

  return (
    <Form.Item label={label} className={styles.file} required={isRequired}>
      {/* Keep form value (file id/url) separate from Upload so AntD Upload onChange does not overwrite it */}
      <Form.Item name={name} rules={rules} noStyle>
        <input type="hidden" />
      </Form.Item>
      {showAspectOptions ? (
        <Segmented
          className={styles.aspectOptions}
          size="small"
          value={aspectKey}
          options={ASPECT_PRESETS.map((preset) => ({
            label: preset.label,
            value: preset.key,
          }))}
          onChange={(value) => setAspectKey(value as AspectPresetKey)}
        />
      ) : null}
      <ImgCrop
        key={aspectKey}
        aspect={cropAspect}
        quality={1}
        modalTitle="Edit image"
        showReset
        aspectSlider
      >
        <Upload
          accept="image/*"
          listType="picture-card"
          fileList={fileList}
          onPreview={handlePreview}
          onRemove={handleRemove}
          // customRequest receives the cropped file from ImgCrop; action(file) gets the original.
          customRequest={({ file }) => {
            fileUpload.fetchFile(file as RcFile);
          }}
          beforeUpload={beforeUpload}
        >
          {fileList.length > 0 ? null : uploadButton}
        </Upload>
      </ImgCrop>
      {description && <div className={styles.description}>{description}</div>}
      {previewImage ? (
        <Image
          wrapperStyle={{ display: 'none' }}
          preview={{
            visible: previewOpen,
            onVisibleChange: (visible) => setPreviewOpen(visible),
            afterOpenChange: (visible) => {
              if (!visible) {
                setPreviewImage('');
              }
            },
          }}
          src={previewImage}
        />
      ) : null}
    </Form.Item>
  );
}
